/**
 * QuoteBlock: `quote` content block.
 *
 * data: { text, attribution?, attributionUrl? }
 *
 * A pull-quote / callout. Degrades gracefully: with no `text` the block
 * renders nothing; a missing `attribution` simply omits the cite line. When
 * `attributionUrl` is a safe link, the attribution becomes a hyperlink to the
 * cited source so readers can navigate to it; otherwise it is plain text.
 */
import React from 'react';
import PropTypes from 'prop-types';

import { isAllowedLinkUrl, isExternalHttpUrl } from '../utils/urlValidator';

export default function QuoteBlock({ data }) {
    const { text, attribution, attributionUrl } = data || {};

    if (!text) {
        return null;
    }

    // Link the attribution only when a URL is supplied AND it passes the URL
    // allowlist (defangs javascript:/data: like the other blocks). Sources are
    // external http(s) wiki pages, so add new-tab + noopener when external.
    const linked =
        Boolean(attribution) &&
        Boolean(attributionUrl) &&
        isAllowedLinkUrl(attributionUrl);

    return (
        <figure className="content-quote ps-4 py-3 my-0">
            <blockquote className="blockquote mb-0">
                <p className="content-quote__text fst-italic mb-0">{text}</p>
            </blockquote>
            {attribution && (
                <figcaption className="content-quote__attribution mb-0">
                    {linked ? (
                        <a
                            href={attributionUrl}
                            {...(isExternalHttpUrl(attributionUrl) && {
                                target: '_blank',
                                rel: 'noopener noreferrer',
                            })}
                        >
                            {attribution}
                        </a>
                    ) : (
                        attribution
                    )}
                </figcaption>
            )}
        </figure>
    );
}

QuoteBlock.propTypes = {
    data: PropTypes.shape({
        text: PropTypes.string,
        attribution: PropTypes.string,
        attributionUrl: PropTypes.string,
    }),
};

QuoteBlock.defaultProps = {
    data: {},
};
